import { useCallback, useMemo, useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import type { GraphHop } from '@solum/experience'
import { useSolum } from '../data/client'

/**
 * The record's relationship graph, one governed HOP at a time (#2324 §4/§6).
 *
 * ── LAZY, BOUNDED, AND EXPLICIT ────────────────────────────────────────────
 *
 * §4: "one hop per user action… no eager recursive crawl." So this holds a LIST
 * of requests — the root, plus one per expansion a person has actually asked
 * for — and each is its own query. Nothing here walks anything: the door has no
 * recursive arm, and expansion is asking it again with a new root.
 *
 * `useQueries` rather than one query with a loop, and the difference is the
 * whole clause:
 *
 *   · each hop CACHES on its own key, so collapsing a branch and re-expanding it
 *     is free rather than a second read of the same records;
 *   · each hop has its own loading and error state, so one refused expansion
 *     does not take the loaded graph down with it;
 *   · a SUPERSEDED read is cancelled by react-query when its key stops being
 *     asked for, which is §4's "cancellation of superseded reads" answered by
 *     the cache rather than by a hand-rolled abort.
 *
 * ── WHAT IS SESSION STATE, DELIBERATELY ────────────────────────────────────
 *
 * The expansion list, the selected node and the current root live HERE and are
 * never written anywhere. §8: "Do not persist transient node coordinates or a
 * record's UUID-specific expansion tree as the governed default. Expansion /
 * selection / root state is session state."
 */
export interface GraphRequest {
  entity: string
  id: string
  /** Per-slot offsets, for a group the person paged further into. */
  cursor?: Record<string, number>
}

export interface GraphHopsState {
  hops: GraphHop[]
  busy: boolean
  problem: string | null
  /** The record the graph is currently about — `Make root` moves it. */
  root: GraphRequest
  makeRoot: (entity: string, id: string) => void
  /** Load one more hop, rooted at a node already on screen. */
  expand: (entity: string, id: string) => void
  /** Drop one expansion, keeping every other loaded hop. */
  collapse: (entity: string, id: string) => void
  /** Page one group of the ROOT hop further. */
  showMore: (slot: string, cursor: number) => void
  /** Is this node one the person has already expanded? */
  isExpanded: (entity: string, id: string) => boolean
}

const keyOf = (r: GraphRequest) => `${r.entity}:${r.id}`

export function useGraphHops(entity: string, id: string, enabled: boolean): GraphHopsState {
  const { client } = useSolum()
  /* The ROOT is state, because `Make root` moves it — §4's "refocus the graph
     around that record". Resetting the expansions with it is deliberate: the
     branches somebody opened around the OLD root are not branches of the new
     one, and carrying them over would draw a graph that is about two records. */
  const [root, setRoot] = useState<GraphRequest>({ entity, id })
  const [children, setChildren] = useState<GraphRequest[]>([])
  /* PAGES are extra hops of the same root, never a new cursor on the existing
     one. Replacing the root's cursor asked for page two INSTEAD of page one —
     the group kept 25 records and swapped which 25 they were, which is not what
     "show more" says. As separate hops they merge, because merging hops is what
     the model already does for an expansion. */
  const [pages, setPages] = useState<GraphRequest[]>([])

  const requests = useMemo(() => [root, ...pages, ...children], [root, pages, children])

  const results = useQueries({
    queries: requests.map((request) => ({
      queryKey: ['graph-hop', request.entity, request.id, request.cursor ?? null],
      enabled: enabled && !!request.entity && !!request.id,
      staleTime: 60_000,
      // Keep the PREVIOUS answer while a new one is in flight. Paging a group
      // changes this hop's key, and without this the whole graph blinks out and
      // comes back as new DOM — which takes keyboard focus to `document.body`
      // with it, the defect `useRelatedPresentation` records one surface over.
      placeholderData: (previous: unknown) => previous,
      queryFn: () => client.raw().rpcScalar<GraphHop>('relationship_graph_hop', {
        p_entity: request.entity,
        p_id: request.id,
        p_groups: null,
        p_limit: null,
        p_cursor: request.cursor ?? null,
      }, 'core'),
    })),
  })

  const hops = useMemo(
    () => results.map((r) => r.data).filter((d): d is GraphHop => !!d && !!d.root),
    [results])

  return {
    hops,
    busy: results.some((r) => r.isLoading),
    // The ROOT failing is the graph failing; a CHILD failing is one branch that
    // did not open, and the rest of the picture is still true. Said as one
    // sentence because the host has one place to say it, but the graph is not
    // taken down for the second case.
    problem: results[0]?.isError
      ? 'This record’s relationships could not be read.'
      : results.slice(1).some((r) => r.isError)
        ? 'One branch could not be expanded.'
        : null,
    root,
    makeRoot: useCallback((e: string, i: string) => {
      setRoot({ entity: e, id: i })
      setChildren([])
      setPages([])
    }, []),
    expand: useCallback((e: string, i: string) => {
      setChildren((current) => current.some((c) => c.entity === e && c.id === i)
        ? current
        : [...current, { entity: e, id: i }])
    }, []),
    collapse: useCallback((e: string, i: string) => {
      setChildren((current) => current.filter((c) => !(c.entity === e && c.id === i)))
    }, []),
    showMore: useCallback((slot: string, cursor: number) => {
      setPages((current) => {
        const next: GraphRequest = { entity: root.entity, id: root.id, cursor: { [slot]: cursor } }
        return current.some((p) => JSON.stringify(p) === JSON.stringify(next))
          ? current : [...current, next]
      })
    }, [root.entity, root.id]),
    isExpanded: useCallback(
      (e: string, i: string) => children.some((c) => keyOf(c) === keyOf({ entity: e, id: i })),
      [children]),
  }
}
